import { Controller } from "@hotwired/stimulus"
import { icon as brandIcon } from "../lib/icons"

export default class extends Controller {
  static targets = [
    "serviceTypeSelect", "itemTypeSelect", "calculationMethodSelect",
    "methodDescription", "methodDescriptionText", "calculationMethodCard",
    "amountConfigCard", "baseAmountField", "baseAmountInput",
    "ratePerUnitField", "ratePerUnitInput", "expenseAccountField",
    "expenseAccountSelect", "expenseAccountRequired",
    "displayOrderInput", "activeToggle", "previewCard", "previewContent",
    "submitButton", "typeDescription", "fixedAmountDescription",
    "proratedExpenseDescription", "meterBasedDescription"
  ]

  static values = {
    condoId: Number,
    templateId: Number,
    serviceTypes: Array
  }

  connect() {
    this.updateFormVisibility()
    this.updateCalculationMethodOptions()

    // Set default active state
    if (!this.activeToggleTarget.checked && this.activeToggleTarget.value === "") {
      this.activeToggleTarget.checked = true
    }
  }

  serviceTypeChanged(event) {
    const serviceTypeId = event.target.value
    const itemType = this.itemTypeSelectTarget.value

    // If item type is fixed_amount and a service type is selected, populate base amount
    if (itemType === 'fixed_amount' && serviceTypeId && this.serviceTypesValue) {
      const serviceType = this.serviceTypesValue.find(st => st.id.toString() === serviceTypeId)
      if (serviceType && serviceType.default_amount && serviceType.default_amount > 0) {
        this.baseAmountInputTarget.value = parseFloat(serviceType.default_amount).toFixed(2)
        // Remove any error styling
        this.baseAmountInputTarget.classList.remove('input-error')
      }
    }

    // Update live preview if form is visible
    this.updateLivePreview()
  }

  itemTypeChanged(event) {
    const itemType = event.target.value
    this.updateCalculationMethodOptions()
    this.updateFormVisibility()
    this.updateRequiredFields()
    this.clearAmountFields()
    this.updateMethodDescription()
    this.updateTypeDescription()

    // If changing to fixed_amount and service type is already selected, populate base amount
    if (itemType === 'fixed_amount') {
      const serviceTypeId = this.serviceTypeSelectTarget.value
      if (serviceTypeId && this.serviceTypesValue) {
        const serviceType = this.serviceTypesValue.find(st => st.id.toString() === serviceTypeId)
        if (serviceType && serviceType.default_amount && serviceType.default_amount > 0) {
          this.baseAmountInputTarget.value = parseFloat(serviceType.default_amount).toFixed(2)
          this.baseAmountInputTarget.classList.remove('input-error')
        }
      }
    }
  }

  calculationMethodChanged(event) {
    const method = event.target.value
    this.updateMethodDescription()
    this.updateRequiredFields()
  }

  updateCalculationMethodOptions() {
    const itemType = this.itemTypeSelectTarget.value
    const methodSelect = this.calculationMethodSelectTarget

    // Clear existing options except prompt
    while (methodSelect.children.length > 1) {
      methodSelect.removeChild(methodSelect.lastChild)
    }

    let options = []

    switch (itemType) {
      case 'fixed_amount':
        options = [
          { value: 'per_unit', text: 'Por Unidad', description: 'Monto fijo igual para todas las unidades' }
        ]
        break
      case 'prorated_expense':
        options = [
          { value: 'per_unit', text: 'Por Unidad', description: 'Distribución equitativa entre todas las unidades' },
          { value: 'by_area', text: 'Por Área', description: 'Distribución proporcional al área de cada unidad' },
          { value: 'by_participation', text: 'Por Participación', description: 'Distribución según coeficiente de participación' }
        ]
        break
      case 'meter_based':
        options = [
          { value: 'by_consumption', text: 'Por Consumo', description: 'Cargo basado en las lecturas del medidor' }
        ]
        break
    }

    // Add options to select
    options.forEach(option => {
      const optionElement = document.createElement('option')
      optionElement.value = option.value
      optionElement.textContent = option.text
      optionElement.setAttribute('data-description', option.description)
      methodSelect.appendChild(optionElement)
    })

    // Auto-select if only one option
    if (options.length === 1) {
      methodSelect.value = options[0].value
      this.updateMethodDescription()
    }
  }

  updateFormVisibility() {
    const itemType = this.itemTypeSelectTarget.value

    // Show/hide amount configuration based on item type
    if (itemType === 'fixed_amount') {
      this.showElement(this.baseAmountFieldTarget)
      this.hideElement(this.ratePerUnitFieldTarget)
      this.hideElement(this.expenseAccountFieldTarget)
    } else if (itemType === 'meter_based') {
      this.hideElement(this.baseAmountFieldTarget)
      this.showElement(this.ratePerUnitFieldTarget)
      this.hideElement(this.expenseAccountFieldTarget)
    } else if (itemType === 'prorated_expense') {
      this.hideElement(this.baseAmountFieldTarget)
      this.hideElement(this.ratePerUnitFieldTarget)
      this.showElement(this.expenseAccountFieldTarget)
    } else {
      this.hideElement(this.baseAmountFieldTarget)
      this.hideElement(this.ratePerUnitFieldTarget)
      this.hideElement(this.expenseAccountFieldTarget)
    }

    // Show method card only if item type is selected
    if (itemType) {
      this.showElement(this.calculationMethodCardTarget)
      this.showElement(this.amountConfigCardTarget)
    } else {
      this.hideElement(this.calculationMethodCardTarget)
      this.hideElement(this.amountConfigCardTarget)
    }
  }

  updateRequiredFields() {
    const itemType = this.itemTypeSelectTarget.value

    // Update expense account requirement
    if (itemType === 'prorated_expense') {
      this.expenseAccountSelectTarget.required = true
      this.showElement(this.expenseAccountRequiredTarget)
    } else {
      this.expenseAccountSelectTarget.required = false
      this.hideElement(this.expenseAccountRequiredTarget)
    }

    // Update amount field requirements
    if (itemType === 'fixed_amount') {
      this.baseAmountInputTarget.required = true
      this.ratePerUnitInputTarget.required = false
    } else if (itemType === 'meter_based') {
      this.baseAmountInputTarget.required = false
      this.ratePerUnitInputTarget.required = true
    } else {
      this.baseAmountInputTarget.required = false
      this.ratePerUnitInputTarget.required = false
    }
  }

  updateMethodDescription() {
    const itemType = this.itemTypeSelectTarget.value
    const calculationMethod = this.calculationMethodSelectTarget.value

    let description = "Selecciona un tipo de concepto para ver las opciones disponibles."

    if (itemType && calculationMethod) {
      const selectedOption = this.calculationMethodSelectTarget.querySelector(`option[value="${calculationMethod}"]`)
      if (selectedOption) {
        description = selectedOption.getAttribute('data-description')
      }
    } else if (itemType) {
      const typeDescriptions = {
        'fixed_amount': 'Los montos fijos se cobran igual a todas las unidades.',
        'prorated_expense': 'Los gastos se distribuyen proporcionalmente entre las unidades.',
        'meter_based': 'Los cargos se calculan según las lecturas de medidores.'
      }
      description = typeDescriptions[itemType] || description
    }

    this.methodDescriptionTextTarget.textContent = description
  }

  updateTypeDescription() {
    const itemType = this.itemTypeSelectTarget.value

    // Hide all descriptions first
    if (this.hasFixedAmountDescriptionTarget) {
      this.hideElement(this.fixedAmountDescriptionTarget)
    }
    if (this.hasProratedExpenseDescriptionTarget) {
      this.hideElement(this.proratedExpenseDescriptionTarget)
    }
    if (this.hasMeterBasedDescriptionTarget) {
      this.hideElement(this.meterBasedDescriptionTarget)
    }

    // Show relevant description
    if (itemType === 'fixed_amount' && this.hasFixedAmountDescriptionTarget) {
      this.showElement(this.fixedAmountDescriptionTarget)
    } else if (itemType === 'prorated_expense' && this.hasProratedExpenseDescriptionTarget) {
      this.showElement(this.proratedExpenseDescriptionTarget)
    } else if (itemType === 'meter_based' && this.hasMeterBasedDescriptionTarget) {
      this.showElement(this.meterBasedDescriptionTarget)
    }
  }

  clearAmountFields() {
    this.baseAmountInputTarget.value = ''
    this.ratePerUnitInputTarget.value = ''
    this.baseAmountInputTarget.classList.remove('input-error')
    this.ratePerUnitInputTarget.classList.remove('input-error')
  }

  validateAmount(event) {
    const input = event.target
    let value = input.value

    // Store cursor position before formatting
    const cursorPosition = input.selectionStart

    // Remove any non-numeric characters except decimal point
    const originalValue = value
    value = value.replace(/[^\d.]/g, '')

    // Ensure only one decimal point
    const parts = value.split('.')
    if (parts.length > 2) {
      value = parts[0] + '.' + parts.slice(1).join('')
    }

    // Limit decimal places to 2
    if (parts[1] && parts[1].length > 2) {
      value = parts[0] + '.' + parts[1].substring(0, 2)
    }

    // Only update value if it actually changed to avoid cursor jumping
    if (originalValue !== value) {
      input.value = value

      // Restore cursor position, adjusting for any removed characters
      const lengthDiff = originalValue.length - value.length
      const newCursorPosition = Math.max(0, cursorPosition - lengthDiff)
      input.setSelectionRange(newCursorPosition, newCursorPosition)
    }

    // Validate amount is positive
    const numValue = parseFloat(value)
    if (value && (isNaN(numValue) || numValue < 0)) {
      input.classList.add('input-error')
    } else {
      input.classList.remove('input-error')
    }

    // Update live preview if configuration is complete
    this.updateLivePreview()
  }

  generatePreview() {
    if (!this.isFormValid()) {
      this.showValidationErrors()
      return
    }

    this.showElement(this.previewCardTarget)
    this.previewContentTarget.innerHTML = '<div class="loading loading-spinner loading-lg mx-auto"></div>'

    const formData = new FormData()
    formData.append('service_type_id', this.serviceTypeSelectTarget.value)
    formData.append('item_type', this.itemTypeSelectTarget.value)
    formData.append('calculation_method', this.calculationMethodSelectTarget.value)
    formData.append('base_amount', this.baseAmountInputTarget.value)
    formData.append('rate_per_unit', this.ratePerUnitInputTarget.value)

    const templateId = this.templateIdValue
    const url = `/billing_templates/${templateId}/items/preview`

    fetch(url, {
      method: 'POST',
      body: formData,
      headers: {
        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content,
        'Accept': 'application/json'
      }
    })
      .then(response => response.json())
      .then(data => {
        this.displayPreview(data)
      })
      .catch(error => {
        console.error('Error generating preview:', error)
        this.previewContentTarget.innerHTML = `
        <div class="alert alert-error">
          <span>Error al generar vista previa. Inténtalo de nuevo.</span>
        </div>
      `
      })
  }

  displayPreview(data) {
    if (!data.valid) {
      this.previewContentTarget.innerHTML = `
        <div class="alert alert-warning">
          <span>${data.error || 'Configuración incompleta para vista previa'}</span>
        </div>
      `
      return
    }

    let previewHtml = `
      <div class="space-y-4">
        <div class="flex justify-between items-center">
          <h4 class="font-semibold">Cálculo: ${data.method}</h4>
          <span class="badge badge-outline">${data.preview.length} unidades</span>
        </div>
        <div class="overflow-x-auto">
          <table class="table table-sm">
            <thead>
              <tr>
                <th>Unidad</th>
    `

    // Add specific headers based on method
    if (data.method === 'by_area') {
      previewHtml += '<th>Área (m²)</th><th>Porcentaje</th>'
    } else if (data.method === 'by_participation') {
      previewHtml += '<th>Participación</th><th>Porcentaje</th>'
    }

    previewHtml += '<th>Monto</th></tr></thead><tbody>'

    // Add preview rows
    data.preview.forEach(unitPreview => {
      previewHtml += `<tr>
        <td class="font-medium">${unitPreview.unit_name}</td>`

      if (data.method === 'by_area') {
        previewHtml += `
          <td>${unitPreview.area || 'N/A'}</td>
          <td>${unitPreview.percentage || 0}%</td>`
      } else if (data.method === 'by_participation') {
        previewHtml += `
          <td>${unitPreview.participation || 'N/A'}</td>
          <td>${unitPreview.percentage || 0}%</td>`
      }

      previewHtml += `<td class="font-mono">S/ ${unitPreview.amount.toFixed(2)}</td></tr>`
    })

    previewHtml += '</tbody></table></div></div>'

    this.previewContentTarget.innerHTML = previewHtml
  }

  updateLivePreview() {
    // Debounce the preview update
    clearTimeout(this.previewTimeout)
    this.previewTimeout = setTimeout(() => {
      if (this.isFormValid() && this.hasPreviewCardTarget && !this.previewCardTarget.classList.contains('hidden')) {
        this.generatePreview()
      }
    }, 500)
  }

  isFormValid() {
    const serviceType = this.serviceTypeSelectTarget.value
    const itemType = this.itemTypeSelectTarget.value
    const calculationMethod = this.calculationMethodSelectTarget.value

    if (!serviceType || !itemType || !calculationMethod) {
      return false
    }

    // Check specific requirements based on item type
    if (itemType === 'fixed_amount') {
      const baseAmount = parseFloat(this.baseAmountInputTarget.value)
      return !isNaN(baseAmount) && baseAmount > 0
    } else if (itemType === 'meter_based') {
      const ratePerUnit = parseFloat(this.ratePerUnitInputTarget.value)
      return !isNaN(ratePerUnit) && ratePerUnit > 0
    } else if (itemType === 'prorated_expense') {
      const expenseAccount = this.expenseAccountSelectTarget.value
      return !!expenseAccount
    }

    return true
  }

  showValidationErrors() {
    // Add visual feedback for validation errors
    if (!this.serviceTypeSelectTarget.value) {
      this.serviceTypeSelectTarget.classList.add('select-error')
    }
    if (!this.itemTypeSelectTarget.value) {
      this.itemTypeSelectTarget.classList.add('select-error')
    }
    if (!this.calculationMethodSelectTarget.value) {
      this.calculationMethodSelectTarget.classList.add('select-error')
    }


    // Show error message
    const errorDiv = document.createElement('div')
    errorDiv.className = 'alert alert-error mb-4'
    errorDiv.innerHTML = `
      <div class="flex">
        ${brandIcon("exclamation-triangle", "w-5 h-5")}
        <div class="ml-2">
          <h4 class="font-semibold">Configuración incompleta</h4>
          <p class="text-sm">Completa todos los campos requeridos para generar la vista previa.</p>
        </div>
      </div>
    `

    this.element.insertBefore(errorDiv, this.element.firstChild)

    // Remove error message after 5 seconds
    setTimeout(() => {
      if (errorDiv.parentNode) {
        errorDiv.remove()
      }
    }, 5000)
  }

  showElement(element) {
    element.classList.remove('hidden')
  }

  hideElement(element) {
    element.classList.add('hidden')
  }
}
